import React, { useEffect, useRef } from "react";
import FormItem from "@components/FormItem/FormItem";
import FormSection from "@components/FormSection/FormSection";
import type {
  FormContext,
  ValidityPeriodType,
} from "@features/port-security/components/SecurityClearence.types";
import {
  getValidityPeriodsOptions,
  isRestrictedNationality,
} from "@features/port-security/components/utils.ts";
import type { PortSecurityFormData } from "@features/port-security/schema/portSecurityFormSchema";
import { usePortSecurityFormStore } from "@features/port-security/store/usePortSecurityForm.store.ts";
import { useFormattedReference } from "@hooks/useFormattedReference";
import { useRestrictedNationalities } from "@hooks/useRestrictedNationalities";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import { REFERENCE_DATA_TYPES } from "@services/referenceDataApi";
import { redirectForTranslateFromEnglishToArabic } from "@utils/translateFromEnglishToArabic.ts";
import { t } from "i18next";
import { FormProvider, useWatch } from "react-hook-form";
import { useOutletContext } from "react-router-dom";

type Company = {
  companyNameEnglish: string;
  companyNameArabic: string;
  companyCode: string;
  proEmail: string;
};

const VisitDetails: React.FC = () => {
  const { securityClearanceRenewID } = useAuthStore();
  const { formData, updateFormData } = useOutletContext<FormContext>();
  const gdprValue = formData.getValues("gdpr");

  const { options: companies } = useFormattedReference<Company>(
    `${REFERENCE_DATA_TYPES.COMPANIES}?isLicensedCompany=true`,
    "bff-gatepass",
  );

  const entryPurposeEnglish = usePortSecurityFormStore((state) => state.entryPurposeEnglish);
  const visitorCompanyEnglish = usePortSecurityFormStore(
    (state) => state.visitorCompanyEnglish ?? "",
  );

  const FOTTEntryOptions = [
    { label: t("common.yes"), value: true },
    { label: t("common.no"), value: false },
  ];

  const presentNationality = useWatch({
    control: formData.control,
    name: "presentNationality",
  });

  const { options: validityPeriods } = useFormattedReference<ValidityPeriodType>(
    REFERENCE_DATA_TYPES.SECURITY_CLEARANCE_VALIDITY_PERIODS,
    "bff-gatepass",
  );

  const { options: restrictedNationalities } = useRestrictedNationalities();

  const validityPeriodsOptions = getValidityPeriodsOptions(
    validityPeriods,
    presentNationality,
    restrictedNationalities,
  );

  const companiesEnOptions = companies.map(({ companyNameEnglish, companyCode }: Company) => ({
    label: companyNameEnglish,
    value: companyCode,
  }));
  const companiesArOptions = companies
    .filter((company) => company.companyNameArabic)
    .map(({ companyNameArabic, companyCode }: Company) => ({
      label: companyNameArabic,
      value: companyCode,
    }));

  const sponsorCompanyEnglish = useWatch({
    control: formData.control,
    name: "sponsorCompanyEnglish",
  });

  const syncingRef = useRef(false);
  const firstSecurityClearanceRenewIDRef = useRef(true);
  const handleSetRequestingSponsorEmail = (companyCode: unknown) => {
    const selectedCompany = companies.find((company) => company.companyCode === companyCode);

    if (selectedCompany?.proEmail) {
      formData.setValue("requestingSponsorEmailAddress", selectedCompany.proEmail, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: false,
      });
      updateFormData("requestingSponsorEmailAddress", selectedCompany.proEmail);
    } else {
      formData.setValue("requestingSponsorEmailAddress", "", {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: false,
      });
      updateFormData("requestingSponsorEmailAddress", "");
    }
  };

  const handleSponsorChange = (
    field: keyof PortSecurityFormData,
    value: string | undefined,
    targetField: keyof PortSecurityFormData,
  ) => {
    if (syncingRef.current) return;

    syncingRef.current = true;
    formData.setValue(targetField, value || "", {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: false,
    });
    updateFormData(field, value || "");
    updateFormData(targetField, value || "");
    queueMicrotask(() => (syncingRef.current = false));
  };

  useEffect(() => {
    handleSponsorChange(
      "sponsorCompanyEnglish",
      sponsorCompanyEnglish || "",
      "sponsorCompanyArabic",
    );
  }, [securityClearanceRenewID, sponsorCompanyEnglish]);

  useEffect(() => {
    if (securityClearanceRenewID && firstSecurityClearanceRenewIDRef.current) {
      firstSecurityClearanceRenewIDRef.current = false;
      return;
    }
    if (isRestrictedNationality(presentNationality, restrictedNationalities)) {
      formData.setValue("securityClearanceValidity", "", {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: false,
      });
      updateFormData("securityClearanceValidity", "");
    }
  }, [presentNationality, restrictedNationalities, securityClearanceRenewID]);

  return (
    <FormProvider {...formData}>
      <FormSection title={t("security-clearance.visit-details")}>
        <div className="col-span-full">
          <FormItem<PortSecurityFormData, "FOTTEntry">
            name="FOTTEntry"
            label={t("security-clearance.fott-entry")}
            type="radio"
            options={FOTTEntryOptions}
            testId="radio-FOTTEntry"
            onFieldChange={updateFormData}
            horizontalRadioButtons
          />
        </div>
        <div className="col-span-full">
          <FormItem<PortSecurityFormData, "entryPurposeEnglish">
            type="textarea"
            maxChars={1000}
            name="entryPurposeEnglish"
            label={t("security-clearance.entry-purpose-en")}
            required
            testId="visitDetails-textarea-entryPurposeEnglish"
            fullWidth
            onFieldChange={updateFormData}
            showCharCounter
          />
        </div>

        <div className="col-span-full">
          <FormItem<PortSecurityFormData, "entryPurposeArabic">
            type="textarea"
            maxChars={1000}
            name="entryPurposeArabic"
            label={t("security-clearance.entry-purpose-ar")}
            dir="rtl"
            testId="visitDetails-textarea-entryPurposeArabic"
            fullWidth
            onFieldChange={updateFormData}
            showCharCounter
            actionInLabel
            textForActionInLabel={t("security-clearance.translate")}
            onActionClicked={() => redirectForTranslateFromEnglishToArabic(entryPurposeEnglish)}
          />
        </div>

        <FormItem<PortSecurityFormData, "visitorCompanyEnglish">
          name="visitorCompanyEnglish"
          maxChars={200}
          label={t("security-clearance.visitor-company-name-en")}
          testId="visitor-company-en"
          onFieldChange={updateFormData}
        />
        <FormItem<PortSecurityFormData, "visitorCompanyArabic">
          name="visitorCompanyArabic"
          maxChars={200}
          dir="rtl"
          label={t("security-clearance.visitor-company-name-ar")}
          testId="visitor-company-ar"
          onFieldChange={updateFormData}
          actionInLabel
          textForActionInLabel={t("security-clearance.translate")}
          onActionClicked={() => redirectForTranslateFromEnglishToArabic(visitorCompanyEnglish)}
        />
        <FormItem<PortSecurityFormData, "sponsorCompanyEnglish">
          name="sponsorCompanyEnglish"
          label={t("security-clearance.requesting-sponsor-company-en")}
          testId="sponsor-company-en"
          type="autocomplete"
          options={companiesEnOptions}
          onFieldChange={(_: string, value: unknown) => {
            handleSponsorChange(
              "sponsorCompanyEnglish",
              (value as string) || undefined,
              "sponsorCompanyArabic",
            );
            handleSetRequestingSponsorEmail(value);
          }}
        />
        <FormItem<PortSecurityFormData, "sponsorCompanyArabic">
          name="sponsorCompanyArabic"
          dir="rtl"
          label={t("security-clearance.requesting-sponsor-company-ar")}
          testId="sponsor-company-ar"
          type="autocomplete"
          options={companiesArOptions}
          onFieldChange={(_: string, value: unknown) =>
            handleSponsorChange(
              "sponsorCompanyArabic",
              (value as string) || undefined,
              "sponsorCompanyEnglish",
            )
          }
        />
        <FormItem<PortSecurityFormData, "requestingSponsorEmailAddress">
          name="requestingSponsorEmailAddress"
          maxChars={200}
          label={t("security-clearance.requesting-sponsor-email-address")}
          testId="requestingSponsorEmailAddress"
          onFieldChange={updateFormData}
        />
        <FormItem<PortSecurityFormData, "uidNumber">
          name="uidNumber"
          maxChars={200}
          label={t("security-clearance.uid-number")}
          testId="uid-number"
          onFieldChange={updateFormData}
        />
        <FormItem<PortSecurityFormData, "securityClearanceValidity">
          name="securityClearanceValidity"
          label={t("security-clearance.security-clearance-validity-period")}
          required
          testId="validity-period"
          type="autocomplete"
          options={validityPeriodsOptions}
          onFieldChange={updateFormData}
        />
        <div className="col-span-full">
          <FormItem<PortSecurityFormData, "gdpr">
            name="gdpr"
            label={t("security-clearance.accept-gdpr")}
            required
            testId="visa-expiry"
            type="checkbox"
            defaultValue={gdprValue}
            onFieldChange={updateFormData}
          />
        </div>
      </FormSection>
    </FormProvider>
  );
};

export default VisitDetails;
